import { useEffect, useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Globe, Plus, Play, Trash2, Pencil, RefreshCw, Package,
  CheckCircle2, XCircle, AlertCircle, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { scraperAPI } from '@/lib/api';

const FRECUENCIAS = [
  { value: 'manual', label: 'Manual (solo cuando lo ejecutes)' },
  { value: 'diaria', label: 'Diaria a la hora indicada' },
  { value: 'semanal', label: 'Semanal (lunes a la hora indicada)' },
];

const EMPTY_FORM = {
  nombre_proveedor: '',
  base_url: '',
  login_url: '',
  email: '',
  password: '',
  urls_catalogo_text: '',
  frecuencia: 'manual',
  hora_ejecucion: '03:00',
  max_pages_per_category: 20,
  activo: true,
};

export default function ScraperProveedores() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // config id en edición
  const [form, setForm] = useState(EMPTY_FORM);
  const [extracting, setExtracting] = useState(null); // id config en extracción
  const [preview, setPreview] = useState(null); // {cfgId, productos, stats}
  const [importing, setImporting] = useState(false);
  const [sugiriendo, setSugiriendo] = useState(false);
  const [sugerencias, setSugerencias] = useState([]); // [{url, categoria}]
  const [progress, setProgress] = useState(null); // {cfgId, categoria_actual, url_actual, paginas_ok, productos_acumulados}

  const cargar = async () => {
    try {
      const res = await scraperAPI.listarConfigs();
      setConfigs(res.data || []);
    } catch {
      toast.error('Error cargando configuraciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const abrirCrear = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const abrirEditar = (cfg) => {
    setEditing(cfg.id);
    setForm({
      nombre_proveedor: cfg.nombre_proveedor || '',
      base_url: cfg.base_url || '',
      login_url: cfg.login_url || '',
      email: cfg.email || '',
      password: '',
      urls_catalogo_text: (cfg.urls_catalogo || []).join('\n'),
      frecuencia: cfg.frecuencia || 'manual',
      hora_ejecucion: cfg.hora_ejecucion || '03:00',
      max_pages_per_category: cfg.max_pages_per_category || 20,
      activo: cfg.activo !== false,
    });
    setShowForm(true);
  };

  const sugerirUrls = async () => {
    if (!form.base_url.trim()) {
      toast.error('Indica primero la URL base');
      return;
    }
    setSugiriendo(true);
    toast.message('🔮 Analizando la web con IA…', {
      description: 'Puede tardar 20-40s; estamos abriendo un navegador headless.',
    });
    try {
      const payload = {
        base_url: form.base_url.trim(),
        login_url: form.login_url.trim() || null,
        email: form.email.trim() || null,
        password: form.password || null,
      };
      // Si estamos editando una config guardada y NO se ha tecleado password, usamos su endpoint
      const res = editing && !form.password
        ? await scraperAPI.sugerirUrls(editing)
        : await scraperAPI.sugerirUrlsAnon(payload);
      const urls = res?.data?.urls_catalogo || [];
      if (urls.length === 0) {
        toast.warning('La IA no detectó URLs claras. Prueba a iniciar sesión o introdúcelas a mano.');
      } else {
        toast.success(`${urls.length} URLs detectadas`);
      }
      setSugerencias(urls);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al analizar la web');
    } finally {
      setSugiriendo(false);
    }
  };

  const aplicarSugerencias = () => {
    const ya = form.urls_catalogo_text.split('\n').map(s => s.trim()).filter(Boolean);
    const set = new Set(ya);
    sugerencias.forEach(s => set.add(s.url));
    setForm({ ...form, urls_catalogo_text: Array.from(set).join('\n') });
    setSugerencias([]);
    toast.success('URLs añadidas al formulario');
  };

  const guardar = async () => {
    const urls = form.urls_catalogo_text.split('\n').map(s => s.trim()).filter(Boolean);
    const payload = {
      nombre_proveedor: form.nombre_proveedor.trim(),
      base_url: form.base_url.trim(),
      login_url: form.login_url.trim() || null,
      email: form.email.trim() || null,
      password: form.password || null,
      urls_catalogo: urls,
      frecuencia: form.frecuencia,
      hora_ejecucion: form.hora_ejecucion,
      max_pages_per_category: parseInt(form.max_pages_per_category) || 20,
      activo: form.activo,
    };
    if (!payload.nombre_proveedor || !payload.base_url || urls.length === 0) {
      toast.error('Nombre, URL base y al menos 1 URL de catálogo son obligatorios');
      return;
    }
    try {
      if (editing) {
        if (!payload.password) delete payload.password; // no sobrescribir si no la cambia
        await scraperAPI.actualizarConfig(editing, payload);
        toast.success('Configuración actualizada');
      } else {
        await scraperAPI.crearConfig(payload);
        toast.success('Proveedor añadido');
      }
      setShowForm(false);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando');
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este proveedor del scraper?')) return;
    try {
      await scraperAPI.eliminarConfig(id);
      toast.success('Eliminado');
      cargar();
    } catch {
      toast.error('Error eliminando');
    }
  };

  const extraer = async (cfg) => {
    setExtracting(cfg.id);
    setProgress(null);
    toast.message(`🤖 Iniciando scraper de ${cfg.nombre_proveedor}…`, {
      description: 'Motor concurrente + selectores cacheados. ETA: 30 s - 3 min.',
    });
    try {
      const res = await scraperAPI.extraer(cfg.id);
      const runId = res.data?.run_id;
      if (!runId) {
        toast.error('No se obtuvo run_id del backend');
        setExtracting(null);
        return;
      }
      // Polling cada 2 s (más rápido) para mostrar progreso en vivo
      const maxAttempts = 600; // 20 min máx
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const r = await scraperAPI.productosRun(runId);
          const status = r.data?.status;
          if (r.data?.progress) {
            setProgress({ cfgId: cfg.id, ...r.data.progress });
          }
          if (status === 'ok') {
            clearInterval(poll);
            setExtracting(null);
            setProgress(null);
            const productos = r.data?.productos || [];
            const stats = r.data?.stats || {};
            setPreview({ cfgId: cfg.id, cfgNombre: cfg.nombre_proveedor, productos, stats });
            if (productos.length === 0) {
              toast.warning('No se extrajeron productos. Revisa URLs/credenciales.');
            } else {
              toast.success(`${productos.length} productos extraídos`);
            }
            cargar();
          } else if (status === 'error') {
            clearInterval(poll);
            setExtracting(null);
            setProgress(null);
            toast.error('El scraper falló. Revisa los logs.');
            cargar();
          } else if (attempts >= maxAttempts) {
            clearInterval(poll);
            setExtracting(null);
            setProgress(null);
            toast.warning('El scraper tarda más de lo esperado. Revisa más tarde en "Última sync".');
          }
        } catch {
          // ignorar errores transitorios
        }
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error iniciando scraper');
      setExtracting(null);
      setProgress(null);
    }
  };

  const importar = async () => {
    if (!preview?.productos?.length) return;
    setImporting(true);
    try {
      const res = await scraperAPI.importar(preview.cfgId, preview.productos);
      const d = res.data || {};
      toast.success(`✅ ${d.creados} nuevos · ${d.actualizados} actualizados`);
      setPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error importando');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="scraper-proveedores-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Globe className="w-7 h-7 text-primary" />
            Scraper de catálogos · IA
          </h1>
          <p className="text-muted-foreground mt-1">
            Importa el catálogo de tus proveedores directamente desde sus webs. Login automático, extracción con IA, importación con upsert.
          </p>
        </div>
        <Button onClick={abrirCrear} data-testid="scraper-nuevo">
          <Plus className="w-4 h-4 mr-1" />
          Añadir proveedor
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Cargando…</p>
      ) : configs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="font-semibold mb-1">Aún no hay proveedores configurados</p>
            <p className="text-sm text-muted-foreground mb-4">
              Añade tu primer proveedor (Utopya, Mobilax, etc.) y pulsa Extraer para importar su catálogo.
            </p>
            <Button onClick={abrirCrear}>
              <Plus className="w-4 h-4 mr-1" />
              Crear primer proveedor
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {configs.map(cfg => (
            <Card key={cfg.id} data-testid={`scraper-config-${cfg.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {cfg.nombre_proveedor}
                      {cfg.activo ? (
                        <Badge variant="outline" className="text-green-700 border-green-300">Activo</Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-500">Pausado</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="break-all">{cfg.base_url}</CardDescription>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => abrirEditar(cfg)} title="Editar">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => eliminar(cfg.id)} title="Eliminar">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-xs text-muted-foreground space-y-1">
                  <div><strong>Frecuencia:</strong> {cfg.frecuencia} {cfg.frecuencia !== 'manual' && `· a las ${cfg.hora_ejecucion}`}</div>
                  <div><strong>URLs catálogo:</strong> {(cfg.urls_catalogo || []).length}</div>
                  {cfg.ultima_sync && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <strong>Última sync:</strong> {new Date(cfg.ultima_sync).toLocaleString('es-ES')}
                    </div>
                  )}
                  {cfg.ultima_sync_stats && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3 text-green-600" />{cfg.ultima_sync_stats.paginas_ok} ok</span>
                      <span className="flex items-center gap-0.5"><XCircle className="w-3 h-3 text-red-600" />{cfg.ultima_sync_stats.paginas_fallo} fallo</span>
                      <span className="flex items-center gap-0.5"><Package className="w-3 h-3" />{cfg.ultima_sync_stats.total_productos} productos</span>
                      <span className="text-muted-foreground">({cfg.ultima_sync_stats.engine})</span>
                    </div>
                  )}
                </div>
                <Button
                  className="w-full"
                  onClick={() => extraer(cfg)}
                  disabled={extracting === cfg.id}
                  data-testid={`btn-extraer-${cfg.id}`}
                >
                  {extracting === cfg.id ? (
                    <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Extrayendo…</>
                  ) : (
                    <><Play className="w-4 h-4 mr-1" /> Extraer ahora</>
                  )}
                </Button>
                {extracting === cfg.id && progress?.cfgId === cfg.id && (
                  <div
                    className="mt-2 p-2 rounded-md bg-blue-50 border border-blue-200 text-[11px] text-blue-900 space-y-0.5"
                    data-testid={`scraper-progress-${cfg.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span><strong>Categoría:</strong> {progress.categoria_actual}</span>
                      <span><strong>{progress.productos_acumulados}</strong> productos</span>
                    </div>
                    <div className="flex items-center gap-2 text-blue-700">
                      <CheckCircle2 className="w-3 h-3" />
                      <span>{progress.paginas_ok} páginas OK</span>
                      {progress.paginas_fallo > 0 && (
                        <>
                          <XCircle className="w-3 h-3 text-red-600" />
                          <span className="text-red-600">{progress.paginas_fallo} fallo</span>
                        </>
                      )}
                    </div>
                    <div className="font-mono text-[10px] truncate text-blue-700/70" title={progress.url_actual}>
                      {progress.url_actual}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal: crear / editar */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar proveedor' : 'Nuevo proveedor'}</DialogTitle>
            <DialogDescription>
              Configura la web del proveedor. Si requiere login, indica las credenciales — se almacenan cifradas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <Label>Nombre del proveedor *</Label>
              <Input value={form.nombre_proveedor} onChange={e => setForm({ ...form, nombre_proveedor: e.target.value })} placeholder="Ej: Utopya" data-testid="form-nombre" />
            </div>
            <div>
              <Label>URL base *</Label>
              <Input value={form.base_url} onChange={e => setForm({ ...form, base_url: e.target.value })} placeholder="https://www.utopya.es" data-testid="form-base-url" />
            </div>
            <div>
              <Label>URL de login (opcional)</Label>
              <Input value={form.login_url} onChange={e => setForm({ ...form, login_url: e.target.value })} placeholder="https://www.utopya.es/login" data-testid="form-login-url" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="form-email" />
              </div>
              <div>
                <Label>Contraseña {editing && <span className="text-xs text-muted-foreground">(vacío = no cambiar)</span>}</Label>
                <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="form-password" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>URLs de catálogo * (una por línea)</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={sugerirUrls}
                  disabled={sugiriendo || !form.base_url.trim()}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50 h-7"
                  data-testid="btn-sugerir-urls"
                >
                  {sugiriendo
                    ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Analizando…</>
                    : '🔮 Sugerir con IA'}
                </Button>
              </div>
              <textarea
                className="w-full min-h-[100px] rounded-md border bg-background px-3 py-2 text-sm"
                value={form.urls_catalogo_text}
                onChange={e => setForm({ ...form, urls_catalogo_text: e.target.value })}
                placeholder={'https://www.utopya.es/161-iphone-repuestos\nhttps://www.utopya.es/162-samsung-repuestos'}
                data-testid="form-urls"
              />
              {sugerencias.length > 0 && (
                <div className="mt-2 p-2 border rounded-md bg-purple-50/40">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-purple-900">
                      {sugerencias.length} URLs sugeridas por IA
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      onClick={aplicarSugerencias}
                      className="h-6 text-xs bg-purple-600 hover:bg-purple-700"
                    >
                      Añadir todas
                    </Button>
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {sugerencias.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="font-mono truncate flex-1" title={s.url}>{s.url}</span>
                        <span className="text-purple-700 whitespace-nowrap">{s.categoria}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Frecuencia</Label>
                <Select value={form.frecuencia} onValueChange={v => setForm({ ...form, frecuencia: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FRECUENCIAS.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hora (UTC)</Label>
                <Input type="time" value={form.hora_ejecucion} onChange={e => setForm({ ...form, hora_ejecucion: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Máx. páginas por categoría</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={form.max_pages_per_category}
                onChange={e => setForm({ ...form, max_pages_per_category: e.target.value })}
                data-testid="form-max-pages"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Paginación automática: el scraper recorre las siguientes páginas hasta este límite (por defecto 20). Aumenta si tu proveedor tiene muchas páginas por categoría.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={guardar} data-testid="form-guardar">
              {editing ? 'Guardar cambios' : 'Crear proveedor'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: previsualizar productos antes de importar */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Previsualizar productos · {preview?.cfgNombre}</DialogTitle>
            <DialogDescription>
              {preview?.productos?.length || 0} productos extraídos por la IA.
              Revisa que sean correctos antes de importar al CRM (regla: upsert por SKU; actualiza precio y nombre).
            </DialogDescription>
          </DialogHeader>
          {preview?.stats && (
            <div className="text-xs text-muted-foreground flex items-center gap-3 px-1">
              <span>Motor: <strong>{preview.stats.engine}</strong></span>
              <span>Páginas OK: {preview.stats.paginas_ok}</span>
              <span>Páginas con fallo: {preview.stats.paginas_fallo}</span>
              {preview.stats.logged_in && <Badge variant="outline" className="border-green-400 text-green-700">Sesión iniciada</Badge>}
            </div>
          )}
          <div className="max-h-[55vh] overflow-y-auto border rounded">
            {(preview?.productos?.length || 0) === 0 ? (
              <p className="text-center text-muted-foreground py-8">No se encontraron productos. Revisa URLs y credenciales.</p>
            ) : (
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Precio</TableHead>
                    <TableHead>Referencia</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(preview?.productos || []).map((p, idx) => (
                    <TableRow key={`${p.sku}-${idx}`}>
                      <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                      <TableCell className="text-sm">{p.nombre}</TableCell>
                      <TableCell className="text-sm">{p.precio?.toFixed(2)}€</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.referencia}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setPreview(null)}>Cerrar</Button>
            <Button
              onClick={importar}
              disabled={importing || !preview?.productos?.length}
              data-testid="btn-importar-preview"
            >
              {importing ? 'Importando…' : <><Package className="w-4 h-4 mr-1" /> Importar al CRM ({preview?.productos?.length || 0})</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Card className="bg-blue-50/50 border-blue-200">
        <CardContent className="py-4 text-sm text-blue-900">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 mt-0.5 text-blue-700 flex-shrink-0" />
            <div>
              <strong>Motor v2 (rápido):</strong> el scraper aprende los selectores CSS
              de la página 1 con IA (Gemini 3 Flash) y reutiliza esos selectores en las
              páginas siguientes con BeautifulSoup directo — 100× más rápido y sin coste
              LLM extra. Descarga hasta 10 páginas en paralelo. Para webs con anti-bot
              usa Playwright (navegador real) con sesión compartida con httpx.
              Los productos se importan por SKU: si existe, se actualiza precio y nombre; si no, se crea con stock=0.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
